/**
 * 회사 전용 캘린더 테마 API (2026-08-25, 콘솔 분리 C단계).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/admin/calendar-themes-company.routes.ts`.
 * 공용(운영자) 테마는 **읽기만** 온다 — 고치고 지우는 것은 우리 회사 테마뿐이다.
 * 사용자가 실제로 고르는 목록은 `/calendar-themes-config`(admin BFF reader) 가 내려주고,
 * 거기에도 이 회사 테마가 함께 실린다.
 */
import { apiClient } from './client';

/** 테마가 제어하는 16개 키 — 서버 화이트리스트와 같아야 한다. */
export const CAL_TOKEN_KEYS = [
  '--cal-grid-bg', '--cal-cell-bg', '--cal-cell-other-bg', '--cal-today-bg', '--cal-border',
  '--cal-header-bg', '--cal-text', '--cal-muted', '--cal-accent',
  '--cal-type-meeting', '--cal-type-personal', '--cal-type-external', '--cal-type-training',
  '--cal-type-todo', '--cal-type-anniversary', '--cal-type-vacation',
] as const;

export const CAL_TOKEN_LABEL: Record<string, string> = {
  '--cal-grid-bg': '격자 배경',
  '--cal-cell-bg': '날짜 칸',
  '--cal-cell-other-bg': '이웃 달 칸',
  '--cal-today-bg': '오늘',
  '--cal-border': '선',
  '--cal-header-bg': '요일 머리',
  '--cal-text': '글자',
  '--cal-muted': '흐린 글자',
  '--cal-accent': '강조',
  '--cal-type-meeting': '회의',
  '--cal-type-personal': '개인',
  '--cal-type-external': '외부',
  '--cal-type-training': '교육',
  '--cal-type-todo': '할 일',
  '--cal-type-anniversary': '기념일',
  '--cal-type-vacation': '휴가',
};

export interface CompanyCalTheme {
  id: number;
  theme_key: string;
  name: string;
  description: string | null;
  tokens: Record<string, string>;
  enabled: boolean;
  scope: string;
  sort_order: number;
  /** 우리 회사가 만든 것인가 — 아니면 운영자가 준 공용 테마다. */
  mine: boolean;
}

export async function listCompanyCalThemes(): Promise<CompanyCalTheme[]> {
  const { data } = await apiClient.get<{ themes: CompanyCalTheme[] }>('/admin/calendar-themes');
  return Array.isArray(data?.themes) ? data.themes : [];
}

export interface CalThemeDraft {
  theme_key?: string;
  name: string;
  description?: string | null;
  tokens: Record<string, string>;
  enabled?: boolean;
}

export async function createCompanyCalTheme(draft: CalThemeDraft): Promise<CompanyCalTheme> {
  const { data } = await apiClient.post<{ theme: CompanyCalTheme }>('/admin/calendar-themes', draft);
  return data.theme;
}

export async function updateCompanyCalTheme(id: number, patch: Partial<CalThemeDraft>): Promise<void> {
  await apiClient.patch(`/admin/calendar-themes/${id}`, patch);
}

/** 쓰는 사람이 있으면 서버가 409 THEME_IN_USE 로 막는다(캘린더가 조용히 기본색이 되는 것 방지). */
export async function deleteCompanyCalTheme(id: number): Promise<void> {
  await apiClient.delete(`/admin/calendar-themes/${id}`);
}
